import * as React from "react"
import { cn } from "../../lib/utils"
import { Sidebar } from "./Sidebar"
import { TopBar } from "./TopBar"
import type { NavItemKey } from "./Sidebar"

const MOBILE_NAV_ITEMS = [
  { key: "dashboard", label: "Home", icon: "dashboard" },
  { key: "guilds", label: "Guilds", icon: "dns" },
  { key: "templates", label: "Templates", icon: "layers" },
] as const

interface AppLayoutProps {
  children: React.ReactNode
  activeItem?: NavItemKey
  onNavigate?: (item: string) => void
  onRefresh?: () => void
  isRefreshing?: boolean
  disabledItems?: Set<string>
  guildName?: string | null
  breadcrumb?: React.ReactNode
  className?: string
}

function AppLayout({
  children,
  activeItem = "guilds",
  onNavigate = () => {},
  onRefresh,
  isRefreshing,
  disabledItems,
  guildName,
  breadcrumb,
  className,
}: AppLayoutProps) {
  return (
    <div className={cn("min-h-screen bg-surface", className)}>
      {/* Top bar -- full width, sticky */}
      <TopBar activeItem={activeItem} onNavigate={onNavigate} onRefresh={onRefresh} isRefreshing={isRefreshing} disabledItems={disabledItems} />

      {/* Sidebar -- fixed left, hidden on mobile */}
      <Sidebar
        activeItem={activeItem}
        onNavigate={onNavigate}
        disabledItems={disabledItems}
        guildName={guildName}
      />

      {/* Main content area */}
      <main className="md:ml-64 p-6 md:p-10 pb-24 md:pb-10">
        {breadcrumb && (
          <div className="mb-6 flex items-center gap-2 text-sm text-on-surface-variant">
            {breadcrumb}
          </div>
        )}
        {children}
      </main>

      {/* Mobile bottom navigation */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface-container-low border-t border-outline-variant/10 flex justify-around items-center py-2 px-1">
        {MOBILE_NAV_ITEMS.map((item) => {
          const isActive = activeItem === item.key
          const isDisabled = disabledItems?.has(item.key)
          return (
            <button
              key={item.key}
              onClick={() => !isDisabled && onNavigate(item.key)}
              disabled={isDisabled}
              className={cn(
                "flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg transition-all duration-150 bg-transparent border-none min-w-[56px]",
                isDisabled
                  ? "text-on-surface-variant/30 cursor-not-allowed"
                  : isActive
                    ? "text-primary-text cursor-pointer"
                    : "text-on-surface-variant cursor-pointer",
              )}
            >
              <span
                className={cn(
                  "material-symbols-outlined text-[22px] transition-all duration-150",
                  isActive && !isDisabled && "bg-primary/15 rounded-full px-4 py-0.5",
                )}
              >
                {item.icon}
              </span>
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          )
        })}
      </nav>
    </div>
  )
}

export { AppLayout, MOBILE_NAV_ITEMS }
export type { AppLayoutProps }
